import { ref } from 'vue';
import { useToast } from 'vue-toastification';

// Singleton state for network status shared across app
const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true);
const hasLoginError = ref(false);
const loginErrorMessage = ref('');
let wasOffline = false;

// Lazy toast instance getter to avoid creating toasts outside components unnecessarily
let toastInstance;
const getToast = () => {
  if (!toastInstance) {
    toastInstance = useToast();
  }
  return toastInstance;
};

export function useNetworkStatus() {
  const handleOnlineStatusChange = () => {
    const currentStatus = typeof navigator !== 'undefined' ? navigator.onLine : true;

    if (!currentStatus && isOnline.value) {
      // Just went offline
      isOnline.value = false;
      wasOffline = true;
    } else if (currentStatus && !isOnline.value && wasOffline) {
      // Just came back online after being offline
      isOnline.value = true;
      // Clear login errors when back online
      clearLoginError();
      // Show success toast that automatically disappears
      getToast().success('Подключение к сети восстановлено', {
        timeout: 3000,
        hideProgressBar: false,
      });
      wasOffline = false;
    } else if (currentStatus && !wasOffline) {
      // Initial online state or stayed online
      isOnline.value = true;
    }
  };

  const setLoginError = (errorMessage) => {
    hasLoginError.value = true;
    loginErrorMessage.value = errorMessage;
  };

  const clearLoginError = () => {
    hasLoginError.value = false;
    loginErrorMessage.value = '';
  };

  const setupNetworkListeners = () => {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);
  };

  const removeNetworkListeners = () => {
    if (typeof window === 'undefined') return;
    window.removeEventListener('online', handleOnlineStatusChange);
    window.removeEventListener('offline', handleOnlineStatusChange);
  };

  return {
    isOnline,
    hasLoginError,
    loginErrorMessage,
    setLoginError,
    clearLoginError,
    setupNetworkListeners,
    removeNetworkListeners,
  };
}
